import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type StepStatus = 'pending' | 'completed' | 'skipped'

export const ONBOARDING_STEPS = [
  { num: 1, label: 'Willkommen' },
  { num: 2, label: 'Accounts' },
  { num: 3, label: 'Positionen' },
  { num: 4, label: 'Sparpläne' },
  { num: 5, label: 'External Assets' },
  { num: 6, label: 'Strategie' },
] as const

export const TOTAL_STEPS = ONBOARDING_STEPS.length

interface OnboardingState {
  currentStep: number
  stepStatus: Record<number, StepStatus>
  /**
   * Wenn true, unterdrückt der Auto-Redirect von /dashboard zu /onboarding.
   * Wird gesetzt, wenn der User den Wizard explizit verlässt. Sobald eine
   * aktive Strategy existiert, ist das Flag ohnehin irrelevant.
   */
  dismissed: boolean
  goNext: () => void
  goPrev: () => void
  goTo: (step: number) => void
  markCompleted: (step: number) => void
  markSkipped: (step: number) => void
  dismiss: () => void
  resume: () => void
  reset: () => void
}

const initialStepStatus: Record<number, StepStatus> = Object.fromEntries(
  ONBOARDING_STEPS.map((s) => [s.num, 'pending' as StepStatus]),
)

export const useOnboardingStore = create<OnboardingState>()(
  persist(
    (set, get) => ({
      currentStep: 1,
      stepStatus: initialStepStatus,
      dismissed: false,
      goNext: () => {
        const next = Math.min(get().currentStep + 1, TOTAL_STEPS)
        set({ currentStep: next })
      },
      goPrev: () => {
        const prev = Math.max(get().currentStep - 1, 1)
        set({ currentStep: prev })
      },
      goTo: (step) => {
        if (step >= 1 && step <= TOTAL_STEPS) set({ currentStep: step })
      },
      markCompleted: (step) =>
        set((s) => ({ stepStatus: { ...s.stepStatus, [step]: 'completed' } })),
      markSkipped: (step) => set((s) => ({ stepStatus: { ...s.stepStatus, [step]: 'skipped' } })),
      dismiss: () => set({ dismissed: true }),
      resume: () => set({ dismissed: false }),
      reset: () => set({ currentStep: 1, stepStatus: { ...initialStepStatus }, dismissed: false }),
    }),
    {
      name: 'sextant-onboarding',
    },
  ),
)
